import React from 'react'
import { Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { formatDateForInput } from '@/lib/dateUtils'
import type { DateRange } from '@/types'

interface DateRangeModalProps {
  dateRange: DateRange
  onDateChange: (type: 'from' | 'to', value: string) => void
  onApply?: () => void
  triggerText?: string
  title?: string
  description?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function DateRangeModal({
  dateRange,
  onDateChange,
  onApply,
  triggerText = "Select Date Range",
  title = "Select Date Range",
  description = "Choose the date range for your report",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange
}: DateRangeModalProps) {
  const [internalOpen, setInternalOpen] = React.useState(false)

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setOpen = controlledOnOpenChange || setInternalOpen

  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const displayText = dateRange.from.toDateString() === dateRange.to.toDateString()
    ? formatDisplayDate(dateRange.from)
    : `${formatDisplayDate(dateRange.from)} - ${formatDisplayDate(dateRange.to)}`

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal"
          >
            <Calendar className="mr-2 h-4 w-4" />
            <span className="truncate">{displayText}</span>
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[425px] w-[calc(100vw-2rem)]">
        <DialogHeader className="space-y-3">
          <DialogTitle className="flex items-center space-x-2 text-lg">
            <Calendar className="h-5 w-5" />
            <span>{title}</span>
          </DialogTitle>
          {description && (
            <DialogDescription className="text-sm">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
       <div className="flex flex-col gap-3 sm:gap-4">
          <div className="space-y-3">
            <Label htmlFor="from" className="text-base font-medium">
              From Date
            </Label>
            <Input
              id="from"
              type="date"
              value={formatDateForInput(dateRange.from)}
              onChange={(e) => onDateChange('from', e.target.value)}
              className="w-full h-12 text-base block"
            />
          </div>
          <div className="space-y-3">
            <Label htmlFor="to" className="text-base font-medium">
              To Date
            </Label>
            <Input
              id="to"
              type="date"
              value={formatDateForInput(dateRange.to)}
              onChange={(e) => onDateChange('to', e.target.value)}
              className="w-full h-12 text-base block"
            />
          </div>
        </div>
        <DialogFooter className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="w-full sm:w-auto h-12 text-base"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (onApply) {
                onApply()
              }
              setOpen(false)
            }}
            className="w-full sm:w-auto h-12 text-base"
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}